import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { Skeleton } from 'primeng/skeleton';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import {
  RequirementOption,
  TicketRequirementItem,
  TicketRevisionItem,
} from '../../../core/models/ticket.model';
import { TicketDevelopmentService } from '../../../core/services/ticket-development.service';
import { utils } from '@app/shared';

@Component({
  selector: 'app-ticket-development',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Button,
    Dialog,
    Select,
    InputText,
    Textarea,
    Skeleton,
    Tag,
    Tooltip,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './ticket-development.component.html',
  styleUrls: ['./ticket-development.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketDevelopmentComponent implements OnInit, OnChanges, OnDestroy {
  private readonly devService = inject(TicketDevelopmentService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly requirementSearchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  @Input() ticketId!: string;
  @Input() canWrite: boolean = false;
  /** Incrementado pelo pai quando chega evento socket de outro usuário/aba */
  @Input() requirementsVersion = 0;
  @Input() revisionsVersion = 0;
  @Output() developmentUpdated = new EventEmitter<{
    requirements: TicketRequirementItem[];
    revisions: TicketRevisionItem[];
  }>();
  @Output() ticketRefreshRequired = new EventEmitter<'requirements' | 'revisions'>();

  // ─── Listas ────────────────────────────────────────────────────────────────
  requirements = signal<TicketRequirementItem[]>([]);
  revisions = signal<TicketRevisionItem[]>([]);
  loadingReqs = signal(false);
  loadingRevs = signal(false);

  // ─── Dialog: Requisito ─────────────────────────────────────────────────────
  showRequirementDialog = signal(false);
  savingReq = signal(false);
  selectedRequirement = signal<string | null>(null);
  requirementOptions = signal<RequirementOption[]>([]);
  loadingReqOptions = signal(false);

  // ─── Dialog: Commit ────────────────────────────────────────────────────────
  showCommitDialog = signal(false);
  savingCommit = signal(false);
  editingCommitId = signal<string | null>(null);
  formIdCommit = signal('');
  formDate = signal('');
  formVersion = signal('');
  formObservation = signal('');

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.requirementSearchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(query => this.loadRequirementOptions(query));

    this.loadRequirements();
    this.loadRevisions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requirementsVersion'] && !changes['requirementsVersion'].firstChange) {
      this.loadRequirements();
    }
    if (changes['revisionsVersion'] && !changes['revisionsVersion'].firstChange) {
      this.loadRevisions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Carga de dados ────────────────────────────────────────────────────────

  async loadRequirements(): Promise<void> {
    if (!this.ticketId) return;
    this.loadingReqs.set(true);
    try {
      const data = await this.devService.getRequirementsByTicket(this.ticketId);
      this.requirements.set(data);
      this.emitUpdated();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao carregar requisitos',
      });
    } finally {
      this.loadingReqs.set(false);
    }
  }

  async loadRevisions(): Promise<void> {
    if (!this.ticketId) return;
    this.loadingRevs.set(true);
    try {
      const data = await this.devService.getRevisionsByTicket(this.ticketId);
      this.revisions.set(data);
      this.emitUpdated();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao carregar revisões',
      });
    } finally {
      this.loadingRevs.set(false);
    }
  }

  // ─── Ações: Requisitos ─────────────────────────────────────────────────────

  async openRequirementDialog(): Promise<void> {
    this.selectedRequirement.set(null);
    this.showRequirementDialog.set(true);
    this.loadingReqOptions.set(true);
    try {
      const options = await this.devService.searchRequirements('');
      this.requirementOptions.set(options);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao carregar requisitos disponíveis',
      });
    } finally {
      this.loadingReqOptions.set(false);
    }
  }

  async onFilterRequirements(event: any): Promise<void> {
    this.requirementSearchSubject.next(event.filter || '');
  }

  private async loadRequirementOptions(query: string = ''): Promise<void> {
    this.loadingReqOptions.set(true);
    try {
      const options = await this.devService.searchRequirements(query);

      // Garantir que o item atualmente selecionado permaneça na lista
      // para evitar que o p-select perca a referência e feche ou mude o valor.
      const currentId = this.selectedRequirement();
      if (currentId) {
        const alreadyInList = options.some(opt => opt.id === currentId);
        if (!alreadyInList) {
          // Se não estiver na lista, tentamos manter o label do que já estava selecionado
          // idealmente teríamos o objeto completo, se não tivermos, a p-select pode mostrar em branco.
          const prevMatch = this.requirementOptions().find(opt => opt.id === currentId);
          if (prevMatch) {
            options.unshift(prevMatch);
          }
        }
      }

      this.requirementOptions.set(options);
    } catch {
      // Silencioso no load de filtro
    } finally {
      this.loadingReqOptions.set(false);
    }
  }

  async saveRequirement(): Promise<void> {
    const reqId = this.selectedRequirement();
    if (!reqId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione um requisito',
      });
      return;
    }

    const isDuplicate = this.requirements().some(r => r.requirement?.id === reqId);
    if (isDuplicate) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Este requisito já foi adicionado a este chamado',
      });
      return;
    }

    this.savingReq.set(true);
    try {
      await this.devService.addRequirement(this.ticketId, reqId);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Requisito adicionado com sucesso',
      });
      this.showRequirementDialog.set(false);
      this.ticketRefreshRequired.emit('requirements');
      await this.loadRequirements();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao adicionar requisito',
      });
    } finally {
      this.savingReq.set(false);
    }
  }

  confirmDeleteRequirement(id: string): void {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja remover este requisito?',
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteRequirement(id),
    });
  }

  async deleteRequirement(id: string): Promise<void> {
    try {
      await this.devService.deleteRequirement(id);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Requisito removido com sucesso',
      });
      this.ticketRefreshRequired.emit('requirements');
      await this.loadRequirements();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao remover requisito',
      });
    }
  }

  // ─── Ações: ID Commits ─────────────────────────────────────────────────────

  openAddCommitDialog(): void {
    this.editingCommitId.set(null);
    this.resetCommitForm();
    this.showCommitDialog.set(true);
  }

  openEditCommitDialog(revision: TicketRevisionItem): void {
    this.editingCommitId.set(revision.id);
    this.formIdCommit.set(revision.id_commit ?? '');
    this.formDate.set(revision.date ? this.toInputDate(revision.date) : '');
    this.formVersion.set(revision.version ?? '');
    this.formObservation.set(revision.observation ?? '');
    this.showCommitDialog.set(true);
  }

  async saveCommit(): Promise<void> {
    const idCommit = this.formIdCommit().trim();
    if (!idCommit) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'O ID Commit é obrigatório',
      });
      return;
    }

    if (!this.formDate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'A data é obrigatória',
      });
      return;
    }

    const editingId = this.editingCommitId();
    const isDuplicate = this.revisions().some(
      r => r.id_commit === idCommit && r.id !== editingId
    );
    if (isDuplicate) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Este ID Commit já foi cadastrado neste chamado',
      });
      return;
    }

    const payload = {
      id_commit: idCommit,
      date: this.formDate(),
      version: this.formVersion().trim() || null,
      observation: this.formObservation().trim() || null,
    };

    this.savingCommit.set(true);
    try {
      if (editingId) {
        await this.devService.updateRevision(editingId, payload);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Commit atualizado com sucesso',
        });
      } else {
        await this.devService.addRevision(this.ticketId, payload);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Commit adicionado com sucesso',
        });
      }
      this.showCommitDialog.set(false);
      this.ticketRefreshRequired.emit('revisions');
      await this.loadRevisions();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao salvar commit',
      });
    } finally {
      this.savingCommit.set(false);
    }
  }

  confirmDeleteCommit(id: string): void {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja remover este commit?',
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteCommit(id),
    });
  }

  async deleteCommit(id: string): Promise<void> {
    try {
      await this.devService.deleteRevision(id);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Commit removido com sucesso',
      });
      this.ticketRefreshRequired.emit('revisions');
      await this.loadRevisions();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao remover commit',
      });
    }
  }

  copyToClipboard(text: string): void {
    utils.copyText(text);
    this.messageService.add({
      severity: 'success',
      summary: 'Copiado',
      detail: 'ID copiado para a área de transferência',
      life: 2000
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  getComplexitySeverity(
    complexity: number | undefined
  ): 'success' | 'warn' | 'danger' | 'secondary' {
    if (complexity == null) return 'secondary';
    if (complexity <= 2) return 'success';
    if (complexity <= 5) return 'warn';
    return 'danger';
  }

  getComplexityLabel(req: RequirementOption): string {
    if (req.complexity_label) return req.complexity_label;
    const labels: Record<number, string> = {
      1: 'Baixa 1h',
      2: 'Baixa 2h',
      3: 'Média 3h',
      4: 'Média 4h',
      5: 'Alta 5h',
      10: 'Complexa 10h',
    };
    return labels[req.complexity ?? 0] ?? String(req.complexity ?? '');
  }

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '';
    try {
      // Evita problema de timezone adicionando hora fixa
      const d = new Date(`${dateStr.slice(0, 10)}T12:00:00`);
      return d.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  }

  // ─── Privados ─────────────────────────────────────────────────────────────

  private toInputDate(dateStr: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    try {
      return new Date(dateStr).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }

  private resetCommitForm(): void {
    this.formIdCommit.set('');
    this.formDate.set('');
    this.formVersion.set('');
    this.formObservation.set('');
  }

  private emitUpdated(): void {
    this.developmentUpdated.emit({
      requirements: this.requirements(),
      revisions: this.revisions(),
    });
  }
}
